/**
 * Burn history management with localStorage persistence
 * Tracks successful token burns for user reference
 */

/** Storage key for burn history - matches ForgeSwap naming convention */
const STORAGE_KEY = "forgeswap:burn_history";

/** Maximum number of burns to store */
const MAX_HISTORY_SIZE = 50;

/**
 * Represents a single burn record
 */
export interface BurnRecord {
  /** Transaction signature */
  signature: string;
  /** Token mint address */
  mintAddress: string;
  /** Token name (if available) */
  tokenName?: string;
  /** Token symbol (if available) */
  tokenSymbol?: string;
  /** Token logo URL (if available) */
  logoURI?: string;
  /** Amount burned (human-readable string) */
  amount: string;
  /** Token decimals */
  decimals: number;
  /** Timestamp when burn occurred (ms since epoch) */
  timestamp: number;
}

/**
 * In-memory fallback when localStorage is unavailable
 */
let memoryFallback: BurnRecord[] = [];

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const testKey = "__test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get all burn records from storage
 * @returns Array of burn records, sorted newest first
 */
export function getBurns(): BurnRecord[] {
  if (!isLocalStorageAvailable()) {
    return [...memoryFallback];
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const burns: BurnRecord[] = JSON.parse(stored);
    // Sort by timestamp descending (newest first)
    return burns.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error("[burnHistory] Error reading from localStorage:", error);
    return [];
  }
}

/**
 * Add a new burn record to history
 * Automatically manages max size by removing oldest entries
 * @param burn - The burn record to add
 */
export function addBurn(burn: BurnRecord): void {
  if (!isLocalStorageAvailable()) {
    // Use memory fallback
    memoryFallback = [burn, ...memoryFallback].slice(0, MAX_HISTORY_SIZE);
    return;
  }

  try {
    const burns = getBurns();

    // Check if this signature already exists (prevent duplicates)
    if (burns.some((b) => b.signature === burn.signature)) {
      return;
    }

    // Add new burn at the beginning
    burns.unshift(burn);

    // Trim to max size
    const trimmed = burns.slice(0, MAX_HISTORY_SIZE);

    // Save to localStorage
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error("[burnHistory] Error writing to localStorage:", error);
    // Fallback to memory
    memoryFallback = [burn, ...memoryFallback].slice(0, MAX_HISTORY_SIZE);
  }
}

/**
 * Clear all burn history
 */
export function clearBurns(): void {
  memoryFallback = [];

  if (!isLocalStorageAvailable()) return;

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("[burnHistory] Error clearing localStorage:", error);
  }
}

/**
 * Format a timestamp into a human-readable "time ago" string
 * @param timestamp - Timestamp in milliseconds since epoch
 * @returns Formatted string like "Just now", "5 minutes ago", etc.
 */
export function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return "Just now";
  }

  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  if (days < 7) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }

  // For older entries, show the actual date
  const date = new Date(timestamp);
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  return `${month} ${day}`;
}

/**
 * Get the count of burns in history
 */
export function getBurnCount(): number {
  return getBurns().length;
}

/**
 * Create a burn record from transaction details
 * Helper function to construct the record object
 */
export function createBurnRecord(
  signature: string,
  mintAddress: string,
  amount: string,
  decimals: number,
  tokenName?: string,
  tokenSymbol?: string,
  logoURI?: string
): BurnRecord {
  return {
    signature,
    mintAddress,
    tokenName,
    tokenSymbol,
    logoURI,
    amount,
    decimals,
    timestamp: Date.now(),
  };
}
